// [pay-currency] BO 주문 화면의 청구 통화 표기 헬퍼.
//
// BE 태깅 계약(OrderDtos.OrderDetailResponse SSoT): Money 필드(paymentAmount/itemAmount/…/
// payableAmount)는 외화 주문이어도 항상 KRW 원장 — ₩ 로 표기한다. 실청구액은 detail 의
// `charge`(ChargeMoney: amount=decimal STRING "11.87", currencyCode) / list 의 additive
// `chargeCurrency`+`chargeAmount` 만 쓴다(KRW 값을 외화로 재태깅 금지).
//
// ★ChargeMoney.amount 는 JSON STRING(Toss-native major decimal) — 부동소수 드리프트 방지를
// 위해 Number() 변환 없이 문자열 그대로 자릿수 그룹핑만 해 표기한다(LOCKED 결정 5/6 과 동일
// 원칙의 표시 버전).

/** 통화 기호. 미지정 통화는 기호 없이 코드 suffix 만으로 표기한다. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  KRW: '₩',
  USD: '$',
  JPY: '¥',
  CNY: '¥',
};

/** 정수부 문자열에 3자리 천단위 콤마 — float 변환 없이 그룹핑. */
const groupDigits = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * decimal STRING("11.87"/"1275") → 그룹핑된 표시 문자열("11.87"/"1,275").
 * 숫자 형태가 아니면(방어) 원문 그대로 반환한다.
 */
const groupDecimalString = (amount: string): string => {
  const m = /^(-?)(\d+)(\.\d+)?$/.exec(amount.trim());
  if (!m) return amount;
  const [, sign, intPart, fracPart] = m;
  return `${sign}${groupDigits(intPart!)}${fracPart ?? ''}`;
};

/**
 * 청구 금액 표기 — "$12.31 USD" / "¥1,275 JPY" / "₩28,000 KRW" 꼴.
 * ¥ 가 JPY/CNY 양쪽 기호라 코드 suffix 로 항상 모호성을 제거한다.
 */
export const formatChargeAmount = (amount: string, currencyCode: string): string => {
  const code = currencyCode.toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code];
  const grouped = groupDecimalString(amount);
  return symbol ? `${symbol}${grouped} ${code}` : `${grouped} ${code}`;
};

/** KRW 원장 금액(₩ 표기). 결제금액 칼럼/행 전용 — "₩28,000". */
export const formatKrwLedger = (amount: number): string => `₩${amount.toLocaleString('ko-KR')}`;

/**
 * T1 동결 환율 표기 — "1 USD = 1,350.25 KRW"(로케일 중립). rate 는 BE BigDecimal(JSON number).
 * 표시 전용(금액 재계산 금지 — BE 동결 환산값이 정본).
 */
export const formatExchangeRate = (rate: number, currencyCode: string): string =>
  `1 ${currencyCode.toUpperCase()} = ${rate.toLocaleString('ko-KR', { maximumFractionDigits: 4 })} KRW`;

/** 통화별 minor 단위 자릿수 — BE CurrencyMinorUnits/ForeignCurrencyScale 미러(지원 통화만). */
const MINOR_UNIT_SCALE: Record<string, number> = {
  KRW: 0,
  USD: 2,
  JPY: 0,
  CNY: 2,
};

/**
 * minor 정수 → major decimal STRING (정확 변환, 반올림 없음 — USD 1187 → "11.87").
 * 결제내역(payment-service)의 amount/refundedAmount 는 minor 정수라 외화 행 표기 폴백에 쓴다.
 * 미지원 통화/비정수는 null(호출측이 legacy raw 표기로 강등).
 */
export const minorToMajorString = (minor: number, currencyCode: string): string | null => {
  const scale = MINOR_UNIT_SCALE[currencyCode.toUpperCase()];
  if (scale == null || !Number.isInteger(minor)) return null;
  if (scale === 0) return String(minor);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor).toString().padStart(scale + 1, '0');
  return `${sign}${abs.slice(0, -scale)}.${abs.slice(-scale)}`;
};

/**
 * 결제(payment-service) 금액 표기 — ★amount/refundedAmount 는 minor-unit 정수(USD 센트:
 * $11.87 → 1187)라 외화 행을 그대로 렌더하면 왜곡된다. 표기 정본은 chargeAmount(major decimal
 * STRING, PaymentSummary additive) + currencyCode; 없으면(미배포/환불액/미지원 통화) minor→major
 * 정확 변환 폴백, 그것도 불가하면 raw + 코드로 강등(화면이 깨지지 않게). KRW 는 ₩ 단독(주문 목록
 * 과 동일 규칙, KRW 는 dp=0 이라 minor==major).
 */
export const formatPaymentMinorAmount = (
  minor: number,
  currencyCode: string,
  chargeAmount?: string | null,
): string => {
  if (currencyCode === 'KRW') return formatKrwLedger(minor);
  const major = chargeAmount ?? minorToMajorString(minor, currencyCode);
  return major != null
    ? formatChargeAmount(major, currencyCode)
    : `${minor.toLocaleString()} ${currencyCode}`;
};

/** detail 응답의 [pay-currency] additive 필드(generated 타입 미반영 — 안전캐스트 대상). */
export interface OrderChargeView {
  /** decimal STRING — USD "11.87" / JPY "1275". */
  readonly amount: string;
  readonly currencyCode: string;
}

/**
 * detail 응답에서 charge/exchangeRate 추출(코드젠 lag 안전캐스트). KRW 주문은 키 자체가
 * 생략(@JsonInclude NON_NULL)이라 null. amount 는 number 로 와도(방어) 문자열화한다.
 */
export const extractOrderCharge = (
  order: unknown,
): { charge: OrderChargeView; exchangeRate: number | null } | null => {
  const o = order as {
    charge?: { amount?: string | number | null; currencyCode?: string | null } | null;
    exchangeRate?: number | string | null;
  };
  const amount = o.charge?.amount;
  const currencyCode = o.charge?.currencyCode;
  if (amount == null || !currencyCode) return null;
  const rate =
    typeof o.exchangeRate === 'string' ? Number(o.exchangeRate) : (o.exchangeRate ?? null);
  return {
    charge: { amount: String(amount), currencyCode },
    exchangeRate: rate != null && Number.isFinite(rate) ? rate : null,
  };
};

/**
 * list 응답의 additive 청구 필드 추출 — be-snapshot 발행 계약(OrderSummaryResponse.charge,
 * 상세와 동일 ChargeMoney 오브젝트 {amount: STRING, currencyCode}). 미배포/KRW 주문은 키
 * 생략 → null = KRW 원장 단독 표기. 방어 폴백: 계약 조율 초기 안내됐던 flat
 * chargeCurrency/chargeAmount 형태도 수용(어느 쪽이 와도 동일 렌더).
 */
export const extractSummaryCharge = (row: unknown): OrderChargeView | null => {
  const r = row as {
    charge?: { amount?: string | number | null; currencyCode?: string | null } | null;
    chargeCurrency?: string | null;
    chargeAmount?: string | number | null;
  };
  if (r.charge?.amount != null && r.charge.currencyCode) {
    return { amount: String(r.charge.amount), currencyCode: r.charge.currencyCode };
  }
  if (r.chargeAmount != null && r.chargeCurrency) {
    return { amount: String(r.chargeAmount), currencyCode: r.chargeCurrency };
  }
  return null;
};
